import BrowserIcon from "@assets/icons/browser.svg";
import MailIcon from "@assets/icons/mail.svg";
import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { useI18n } from "@core/i18n/i18n";
import { analyticsService, networkActionsErrorsManager, userManager } from "@core/service-container";
import { AnalyticsCategory } from "@domain/analytics/analytics-types";
import { NotificationPreferences, PtNotificationPreferencesType } from "@domain/user/user";
import styled from "@emotion/styled";
import { ToastContext } from "@ui/components/common/toast-provider";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { NotificationPreferencesRow } from "@ui/pages/settings/components/notification-preferences-row";
import { physioMeTabletBreakpoint, row, stack } from "@ui/style";
import { Button } from "primereact/button";
import React, { useCallback, useContext, useMemo, useRef, useState } from "react";

type PreferenceType = {
	label: string;
	type: PtNotificationPreferencesType;
};

type PreferenceGroupType = {
	title?: string;
	preferenceTypes: PreferenceType[];
};

export const NotificationPreferencesTab = (): JSX.Element => {
	const { format } = useI18n();
	const { showToast } = useContext(ToastContext);
	const { isDesktop } = useDimensions();

	const [notificationPreferences, setNotificationPreferences] = useState<NotificationPreferences[]>(
		userManager.user.get()?.notificationPreferences ?? [],
	);

	const savedNotificationPreferences = useRef<NotificationPreferences[]>(
		userManager.user.get()?.notificationPreferences ?? [],
	);

	const findPreferenceByType = (type: PtNotificationPreferencesType): NotificationPreferences | undefined =>
		notificationPreferences.find(pref => pref.type === type);

	const changePreferenceType = (changedPref: NotificationPreferences) => {
		const updatedNotificationPreferences = notificationPreferences.map(pref => {
			if (pref.type === changedPref.type) {
				return {
					...pref,
					web: changedPref.web,
					email: changedPref.email,
				};
			}
			return pref;
		});
		setNotificationPreferences(updatedNotificationPreferences);
	};

	function sendAnalyticsOnDifferences(oldPrefs: NotificationPreferences[], newPrefs: NotificationPreferences[]) {
		newPrefs.forEach(newPref => {
			const oldPref = oldPrefs.find(p => p.type === newPref.type);
			if (newPref.web !== oldPref?.web) {
				analyticsService.trackEvent({
					category: AnalyticsCategory.NOTIFICATIONS,
					action: "modifyNotificationPreferences",
					params: [newPref.type, "Web", newPref.web],
				});
			}
			if (newPref.email !== oldPref?.email) {
				analyticsService.trackEvent({
					category: AnalyticsCategory.NOTIFICATIONS,
					action: "modifyNotificationPreferences",
					params: [newPref.type, "Email", newPref.email],
				});
			}
		});
	}

	const [savePreferences, isLoading] = useAsyncOperation(
		useCallback(async () => {
			try {
				await userManager.updateNotificationPreferences(notificationPreferences);
				showToast(
					format("settings.notificationPreferences.save.successToast.title"),
					format("settings.notificationPreferences.save.successToast.message"),
					"success",
				);

				sendAnalyticsOnDifferences(savedNotificationPreferences.current, notificationPreferences);
				savedNotificationPreferences.current = [...notificationPreferences];
			} catch (e) {
				showToast(
					format("settings.notificationPreferences.save.errorToast.title"),
					format("settings.notificationPreferences.save.errorToast.message"),
					"error",
				);
				networkActionsErrorsManager.reportError(e);
			}
		}, [format, notificationPreferences, showToast]),
		{},
	);

	const preferencesGroups: PreferenceGroupType[] = useMemo(
		() => [
			{
				preferenceTypes: [
					{
						label: format("settings.notificationPreferences.checkboxes.physiomeEmails"),
						type: PtNotificationPreferencesType.PHYSIO_ME,
					},
					{
						label: format("settings.notificationPreferences.checkboxes.biogenEmails"),
						type: PtNotificationPreferencesType.BIOGEN,
					},
				],
			},
			{
				title: format("settings.notificationPreferences.title.patientRelated"),
				preferenceTypes: [
					{
						label: format("settings.notificationPreferences.checkboxes.patientProgramStarted"),
						type: PtNotificationPreferencesType.PATIENT_PROGRAM_STARTED,
					},
					{
						label: format("settings.notificationPreferences.checkboxes.patientProgramEndingSoon"),
						type: PtNotificationPreferencesType.PATIENT_PROGRAM_ENDING_SOON,
					},
					{
						label: format("settings.notificationPreferences.checkboxes.patientProgramEnded"),
						type: PtNotificationPreferencesType.PATIENT_PROGRAM_ENDED,
					},
					{
						label: format("settings.notificationPreferences.checkboxes.patientRegistered"),
						type: PtNotificationPreferencesType.PATIENT_REGISTERED,
					},
					{
						label: format("settings.notificationPreferences.checkboxes.monthlyRecap"),
						type: PtNotificationPreferencesType.MONTHLY_RECAP,
					},
				],
			},
			{
				title: format("settings.notificationPreferences.title.hctRelated"),
				preferenceTypes: [
					{
						label: format("settings.notificationPreferences.checkboxes.hctInvitationAccepted"),
						type: PtNotificationPreferencesType.HCT_INVITATION_ACCEPTED,
					},
				],
			},
		],
		[format],
	);

	return (
		<Container>
			<RowTitle>
				<h3>{format("settings.notificationPreferences.title.physiomeUpdates")}</h3>
				{isDesktop && (
					<ColumnTitleContainer>
						<ColumnTitle className="p-size-s-bold">
							{format("settings.notificationPreferences.emailColumn")}
							<MailIcon width={16} height={16} />
						</ColumnTitle>
						<ColumnTitle className="p-size-s-bold">
							{format("settings.notificationPreferences.webColumn")}
							<BrowserIcon width={16} height={16} />
						</ColumnTitle>
					</ColumnTitleContainer>
				)}
			</RowTitle>
			{preferencesGroups.map(group => (
				<div key={group.title ?? group.preferenceTypes.map(({ type }) => type).join("-")}>
					{group.title && <Title>{group.title}</Title>}
					{group.preferenceTypes.map(pref => (
						<NotificationPreferencesRow
							key={pref.type}
							label={pref.label}
							preference={findPreferenceByType(pref.type)}
							onChange={p => changePreferenceType({ ...p, type: p.type })}
						/>
					))}
				</div>
			))}
			<ButtonContainer>
				<StyledButton
					label={format("settings.notificationPreferences.saveButton")}
					onClick={() => savePreferences()}
					loading={isLoading}
				/>
			</ButtonContainer>
		</Container>
	);
};

const Container = styled.div`
	display: flex;
	flex-direction: column;
	@media ${physioMeTabletBreakpoint} {
		margin: 32px 24px;
	}
`;

const RowTitle = styled.div`
	${row(undefined, "spacebetween", "center")};
	margin-top: 16px;
	margin-bottom: 24px;

	@media ${physioMeTabletBreakpoint} {
		${stack(undefined, "center", "flexstart")};
		margin-top: 0;
	}
`;

const Title = styled.h3`
	margin-top: 40px;
	margin-bottom: 24px;

	@media ${physioMeTabletBreakpoint} {
		margin-top: 8px;
		margin-bottom: 16px;
	}
`;

const ColumnTitleContainer = styled.div`
	${row(undefined, "spacearound", "center")};
	width: 180px;
`;

const ColumnTitle = styled.div`
	${row(undefined, "center", "center")};
	gap: 8px;
	width: 90px;
`;

const ButtonContainer = styled.div`
	${row(undefined, "flexend")}
	margin-top: 40px;

	@media ${physioMeTabletBreakpoint} {
		margin-top: 8px;
	}
`;

const StyledButton = styled(Button)`
	min-width: 180px;

	@media ${physioMeTabletBreakpoint} {
		min-width: unset;
	}
`;
